/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — Print / Export (rebuild).

   Josh asked for this personally: a plotter-size-aware print/export so
   the chart can go out on the office's 11x17 printer or the plotter.
   Read-only action — available to view-only users too.

   REBUILD of `team/orgchart/OrgChartPrint.tsx` (2026-08-17 audit closed
   5 root causes — see PEOPLE_REBUILD_INVENTORY.md §6):
     1. html2canvas serializes each `<svg>` via XMLSerializer into a
        `data:` URL, where CSS `var()` is unresolvable → edge strokes
        rendered `none` → lines invisible. FIX: `html-to-image` clones
        the DOM and inlines COMPUTED styles onto the clone before
        serializing, so every `var(--token)` is already a concrete
        value by the time anything is rasterized.
     2. Per-edge `<svg>` relies on `overflow: visible` with a tiny
        layout box; html2canvas crops to that box. FIX: html-to-image's
        clone preserves the real computed `overflow` on every node, no
        separate SVG-serialization step to crop against.
     3. Fixed 2×rAF+120ms capture delay with no measured-nodes gate →
        edges could be null on cold mounts. FIX: `CaptureSheet` gates on
        `useNodesInitialized()` AND a DOM poll for one
        `.react-flow__edge-path` per edge before signaling ready.
     4. html2canvas was only an *optional* dep of jsPDF, not installed
        directly. FIX: `html-to-image` is a direct dependency.
     5. Landscape never reached the print job (only jsPDF got explicit
        dimensions). FIX: desktop now round-trips through a typed IPC
        channel straight into `webContents.print({ landscape })`; web
        fallback improved (see `printJob.ts`).

   APPROACH (unchanged from the old dialog): rasterize a second, headless
   `<ReactFlow>` instance instead of re-deriving SVG from node/edge data —
   the custom edge router (hops, junctions, waypoints, labels) already
   renders correctly on-screen, so screenshotting the real components
   guarantees pixel parity with the live canvas.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Node, Edge } from '@xyflow/react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { Printer, Download } from 'lucide-react';
import { Icon } from '../../../../components/ui/Icon';
import { rem } from '../../../../lib/rem';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Checkbox } from '../../../../components/ui/checkbox';
import type { Team } from '../../../../../shared/types';
import { AuthService } from '../../../../services/authService';
import { CaptureSheet, type CaptureJob } from './CaptureSheet';
import { printChartImage, hasDesktopPdfBridge, exportChartPdf } from './printJob';
import { buildSheetHtmlDocument } from './serializeSheetHtml';
import { PRESETS, type PresetKey, paperInches, computeSheetLayout, todayIso } from './geometry';

export interface PrintExportButtonProps {
  nodes: Node[];
  edges: Edge[];
  teams: Team[];
}

/** Toolbar entry point — Lucide printer icon, kit Button, always visible
 *  (printing is a read operation; view-only users get it same as editors). */
export function PrintExportButton({ nodes, edges, teams }: PrintExportButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => setOpen(true)}
        title="Print or export the org chart"
        aria-label="Print or export the org chart"
      >
        <Icon icon={Printer} em={1.2308} aria-hidden="true" />
      </Button>
      {open && (
        <PrintDialog nodes={nodes} edges={edges} teams={teams} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function PrintDialog({
  nodes,
  edges,
  teams,
  onClose,
}: PrintExportButtonProps & { onClose: () => void }) {
  const [preset, setPreset] = useState<PresetKey>('tabloid');
  const [customW, setCustomW] = useState(24);
  const [customH, setCustomH] = useState(36);
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [scaleMode, setScaleMode] = useState<'fit' | 'actual'>('fit');
  const [includeTeamBoxes, setIncludeTeamBoxes] = useState(true);
  const [includeLegend, setIncludeLegend] = useState(teams.length > 0);
  const [title, setTitle] = useState('Org Chart');
  const [firmName, setFirmName] = useState('SpecForma');
  const [busy, setBusy] = useState<'print' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // Live preview (Josh grant 2026-08-19: "make the print preview for PDFs
  // better") — genuinely representative, not an approximation: it runs the
  // EXACT same headless-sheet + html-to-image capture the real Print/"Save
  // as PDF" raster path uses (captureSheetToDataUrl below), just rendered
  // into an <img> instead of handed to the OS/jsPDF. The vector desktop PDF
  // serializes the SAME sheetRef DOM (serializeSheetHtml.ts), so this stays
  // visually faithful to that output too. Regenerates on every setting
  // change, debounced so rapid typing/toggling doesn't thrash the
  // rasterizer.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewSeqRef = useRef(0);

  // Firm branding source: this app has no stored firm LOGO anywhere
  // (checked Firm entity, AdminPage, auth service — name only). Degrade
  // to the firm NAME as text in the top-right corner instead of building
  // a new logo-upload feature (unchanged from the old dialog).
  useEffect(() => {
    AuthService.getUser()
      .then((u) => {
        if (u?.firmName) setFirmName(u.firmName);
      })
      .catch(() => {});
  }, []);

  const [capture, setCapture] = useState<CaptureJob | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const resolveRef = useRef<((payload: string) => void) | null>(null);
  const rejectRef = useRef<((err: Error) => void) | null>(null);
  // What the in-flight capture should resolve to — the raster PNG path
  // (print, and web "Save as PDF") or the desktop vector-PDF HTML path.
  const captureModeRef = useRef<
    { kind: 'raster' } | { kind: 'html'; widthIn: number; heightIn: number }
  >({ kind: 'raster' });

  // Force the whole document to light theme for the duration of a capture
  // (restored on unmount/next-tick) — the app's tokens flip in dark mode,
  // and re-deriving every token by hand here would drift from base.css.
  useEffect(() => {
    if (!capture) return;
    const html = document.documentElement;
    const prevTheme = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'light');
    return () => {
      if (prevTheme) html.setAttribute('data-theme', prevTheme);
      else html.removeAttribute('data-theme');
    };
  }, [capture]);

  function buildCaptureJob(wIn: number, hIn: number): CaptureJob {
    const drawNodes = includeTeamBoxes ? nodes : nodes.filter((n) => n.type !== 'teamBox');
    const keepIds = new Set(drawNodes.map((n) => n.id));
    const drawEdges = edges.filter((e) => keepIds.has(e.source) && keepIds.has(e.target));
    const showLegend = includeLegend && teams.length > 0;
    const layout = computeSheetLayout(wIn, hIn, drawNodes, scaleMode, showLegend);
    return {
      widthPx: layout.widthPx,
      heightPx: layout.heightPx,
      viewport: layout.viewport,
      nodes: drawNodes,
      edges: drawEdges,
      title: title.trim() || 'Org Chart',
      firmName,
      showLegend,
      teams,
    };
  }

  /** Mounts the headless sheet, waits for the measured-nodes + painted-edges
   *  gate (see CaptureSheet), then rasterizes it with html-to-image. Used
   *  for Print (both platforms) and "Save as PDF" on web. */
  function captureSheetToDataUrl(job: CaptureJob): Promise<string> {
    return new Promise((resolve, reject) => {
      captureModeRef.current = { kind: 'raster' };
      resolveRef.current = resolve;
      rejectRef.current = reject;
      setCapture(job);
    });
  }

  // Debounced live preview — regenerates whenever a setting that changes
  // the sheet changes. Skips while a real export or another preview
  // capture is already using the shared headless-sheet singleton
  // (`capture`/`sheetRef`/resolveRef` above) so the two paths never
  // clobber each other's in-flight promise.
  useEffect(() => {
    if (busy) return;
    const seq = ++previewSeqRef.current;
    const t = setTimeout(async () => {
      if (busy || previewBusy) return;
      setPreviewBusy(true);
      setPreviewError(null);
      try {
        const { w: wIn, h: hIn } = paperInches(preset, customW, customH, orientation);
        const job = buildCaptureJob(wIn, hIn);
        const dataUrl = await captureSheetToDataUrl(job);
        if (seq === previewSeqRef.current) setPreviewUrl(dataUrl);
      } catch (err) {
        if (seq === previewSeqRef.current) {
          setPreviewError(
            err instanceof Error && err.message ? err.message : 'Could not generate a preview.'
          );
        }
      } finally {
        setPreviewBusy(false);
      }
    }, 450);
    return () => clearTimeout(t);
    // `nodes`/`edges` deliberately excluded — the live canvas re-renders
    // (hover, multiplayer cursors, snap guides) far more often than every
    // 450ms and hands this dialog a NEW array reference each time, which
    // would keep resetting the debounce forever and the preview would
    // never settle. The dialog captures a snapshot of the board when it
    // opened; only the SETTINGS below need to re-trigger a preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    preset,
    customW,
    customH,
    orientation,
    scaleMode,
    includeTeamBoxes,
    includeLegend,
    title,
    firmName,
  ]);

  /** Desktop-only vector path: same headless mount + readiness gate as
   *  the raster path above, but resolves a self-contained HTML document
   *  of the sheet (see serializeSheetHtml.ts) instead of a PNG. */
  function captureSheetToHtml(job: CaptureJob, widthIn: number, heightIn: number): Promise<string> {
    return new Promise((resolve, reject) => {
      captureModeRef.current = { kind: 'html', widthIn, heightIn };
      resolveRef.current = resolve;
      rejectRef.current = reject;
      setCapture(job);
    });
  }

  async function handleCaptureReady(job: CaptureJob) {
    try {
      if (document.fonts?.ready) await document.fonts.ready.catch(() => {});
      // One more paint frame after the ready-gate + fonts settle.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (!sheetRef.current) throw new Error('The capture sheet did not mount.');
      const mode = captureModeRef.current;
      if (mode.kind === 'html') {
        const html = buildSheetHtmlDocument(sheetRef.current, job, mode.widthIn, mode.heightIn);
        resolveRef.current?.(html);
      } else {
        const dataUrl = await toPng(sheetRef.current, {
          width: job.widthPx,
          height: job.heightPx,
          pixelRatio: 1,
          backgroundColor: '#ffffff',
          cacheBust: true,
        });
        resolveRef.current?.(dataUrl);
      }
    } catch (err) {
      rejectRef.current?.(err instanceof Error ? err : new Error('Could not prepare the chart.'));
    } finally {
      resolveRef.current = null;
      rejectRef.current = null;
      setCapture(null);
    }
  }

  async function handlePrint() {
    setBusy('print');
    setError(null);
    setStatus(null);
    try {
      const { w: wIn, h: hIn } = paperInches(preset, customW, customH, orientation);
      const job = buildCaptureJob(wIn, hIn);
      const dataUrl = await captureSheetToDataUrl(job);
      await printChartImage(dataUrl, wIn, hIn, orientation === 'landscape');
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not prepare the chart for printing. Try again.'
      );
    } finally {
      setBusy(null);
    }
  }

  async function handleSavePdf() {
    setBusy('pdf');
    setError(null);
    setStatus(null);
    try {
      const { w: wIn, h: hIn } = paperInches(preset, customW, customH, orientation);
      const job = buildCaptureJob(wIn, hIn);
      const suggestedName = `org-chart-${todayIso()}.pdf`;

      if (hasDesktopPdfBridge()) {
        // Desktop: TRUE VECTOR PDF — Chromium's own print engine renders
        // the REAL sheet DOM (crisp paths, selectable text) instead of a
        // rasterized image.
        const html = await captureSheetToHtml(job, wIn, hIn);
        const result = await exportChartPdf(html, wIn, hIn, suggestedName);
        if (result.canceled) {
          // Quiet reset — the user backed out of the native save dialog.
        } else if (!result.success) {
          setError(result.error || 'Could not generate the PDF. Try again.');
        } else {
          setStatus(result.path ? `Saved to ${result.path}` : 'PDF saved.');
        }
        return;
      }

      // Web: unchanged raster path — heavy PDF fidelity is desktop's job.
      const dataUrl = await captureSheetToDataUrl(job);
      // jsPDF defaults to portrait and silently swaps format dims to fit —
      // orientation must be explicit or landscape sheets come out portrait.
      const pdf = new jsPDF({
        unit: 'in',
        format: [wIn, hIn],
        orientation: wIn > hIn ? 'landscape' : 'portrait',
      });
      pdf.addImage(dataUrl, 'PNG', 0, 0, wIn, hIn);
      pdf.save(suggestedName);
    } catch (err) {
      setError(
        err instanceof Error && err.message ? err.message : 'Could not generate the PDF. Try again.'
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Print / export org chart</DialogTitle>
          <DialogDescription>
            Sized for the office plotter or the 11×17 printer. Every member and line is included,
            fit to the sheet you pick — not just what's on screen. The preview on the right is the
            actual sheet that will print or save.
          </DialogDescription>
        </DialogHeader>

        <div
          style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'flex-start', minWidth: 0 }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
              width: rem(300),
              flexShrink: 0,
            }}
          >
            {/* Paper preset */}
            <div>
              <Label>Paper size</Label>
              <div
                role="radiogroup"
                aria-label="Paper size"
                style={{ display: 'flex', flexWrap: 'wrap', gap: rem(6), marginTop: rem(6) }}
              >
                {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={preset === key}
                    onClick={() => setPreset(key)}
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: preset === key ? 600 : 500,
                      background: preset === key ? 'var(--ac)' : 'var(--sf)',
                      color: preset === key ? 'var(--color-text-inverse)' : 'var(--tx)',
                      border: '1px solid var(--bd)',
                      borderRadius: rem(8),
                      cursor: 'pointer',
                    }}
                  >
                    {PRESETS[key].label}
                  </button>
                ))}
                <button
                  type="button"
                  role="radio"
                  aria-checked={preset === 'custom'}
                  onClick={() => setPreset('custom')}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: preset === 'custom' ? 600 : 500,
                    background: preset === 'custom' ? 'var(--ac)' : 'var(--sf)',
                    color: preset === 'custom' ? 'var(--color-text-inverse)' : 'var(--tx)',
                    border: '1px solid var(--bd)',
                    borderRadius: rem(8),
                    cursor: 'pointer',
                  }}
                >
                  Custom
                </button>
              </div>
              {preset === 'custom' && (
                <div
                  style={{
                    display: 'flex',
                    gap: rem(10),
                    alignItems: 'center',
                    marginTop: 'var(--space-2)',
                  }}
                >
                  <Input
                    type="number"
                    min={1}
                    value={customW}
                    onChange={(e) => setCustomW(Number(e.target.value) || 1)}
                    style={{ width: rem(90) }}
                    aria-label="Custom width in inches"
                  />
                  <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>× </span>
                  <Input
                    type="number"
                    min={1}
                    value={customH}
                    onChange={(e) => setCustomH(Number(e.target.value) || 1)}
                    style={{ width: rem(90) }}
                    aria-label="Custom height in inches"
                  />
                  <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>
                    inches
                  </span>
                </div>
              )}
            </div>

            {/* Orientation + scale */}
            <div style={{ display: 'flex', gap: 'var(--space-5)' }}>
              <div>
                <Label>Orientation</Label>
                <div style={{ display: 'flex', gap: rem(6), marginTop: rem(6) }}>
                  {(['landscape', 'portrait'] as const).map((o) => (
                    <button
                      key={o}
                      type="button"
                      role="radio"
                      aria-checked={orientation === o}
                      onClick={() => setOrientation(o)}
                      style={{
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: orientation === o ? 600 : 500,
                        background: orientation === o ? 'var(--ac)' : 'var(--sf)',
                        color: orientation === o ? 'var(--color-text-inverse)' : 'var(--tx)',
                        border: '1px solid var(--bd)',
                        borderRadius: rem(8),
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Scale</Label>
                <div style={{ display: 'flex', gap: rem(6), marginTop: rem(6) }}>
                  {(
                    [
                      { v: 'fit', label: 'Fit to page' },
                      { v: 'actual', label: 'Actual size' },
                    ] as const
                  ).map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      role="radio"
                      aria-checked={scaleMode === o.v}
                      onClick={() => setScaleMode(o.v)}
                      style={{
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: scaleMode === o.v ? 600 : 500,
                        background: scaleMode === o.v ? 'var(--ac)' : 'var(--sf)',
                        color: scaleMode === o.v ? 'var(--color-text-inverse)' : 'var(--tx)',
                        border: '1px solid var(--bd)',
                        borderRadius: rem(8),
                        cursor: 'pointer',
                      }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {scaleMode === 'actual' && (
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--tm)',
                      marginTop: 'var(--space-1)',
                    }}
                  >
                    Prints at 1:1 — a large chart may not fit one sheet.
                  </p>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="orgchart-print-title">Title</Label>
              <Input
                id="orgchart-print-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Org Chart"
                style={{ marginTop: rem(6) }}
              />
            </div>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                }}
              >
                <Checkbox checked={includeTeamBoxes} onCheckedChange={setIncludeTeamBoxes} />
                <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                  Include team-box backdrops
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  cursor: 'pointer',
                }}
              >
                <Checkbox
                  checked={includeLegend}
                  onCheckedChange={setIncludeLegend}
                  disabled={teams.length === 0}
                />
                <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                  Include team color legend
                </span>
              </label>
            </div>

            {error && (
              <p role="alert" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--dg)' }}>
                {error}
              </p>
            )}
            {status && !error && (
              <p role="status" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--ok)' }}>
                {status}
              </p>
            )}
          </div>

          {/* Live preview — same raster capture as the real Print/"Save as
            PDF" path (see the debounced effect above), just shown instead
            of dispatched. Orientation/aspect ratio come straight from the
            image's own intrinsic size (wIn×hIn, computed the same way the
            real export sizes the sheet), so a landscape pick always
            previews as a wide sheet, never stretched/cropped. */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: rem(360),
              maxHeight: rem(480),
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-3)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {previewUrl && (
              <img
                src={previewUrl}
                alt={`Preview of the ${title.trim() || 'Org Chart'} sheet`}
                style={{
                  maxWidth: '100%',
                  maxHeight: rem(456),
                  objectFit: 'contain',
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
                  opacity: previewBusy ? 0.5 : 1,
                  transition: 'opacity 120ms ease',
                }}
              />
            )}
            {!previewUrl && !previewBusy && !previewError && (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                Preparing preview…
              </span>
            )}
            {previewBusy && !previewUrl && (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                Generating preview…
              </span>
            )}
            {previewError && (
              <span
                role="alert"
                style={{ fontSize: 'var(--font-size-sm)', color: 'var(--dg)', textAlign: 'center' }}
              >
                {previewError}
              </span>
            )}
            {previewBusy && previewUrl && (
              <span
                style={{
                  position: 'absolute',
                  bottom: 'var(--space-2)',
                  right: 'var(--space-3)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--tm)',
                  background: 'var(--sa)',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-full)',
                  padding: rem(2) + ' var(--space-2)',
                }}
              >
                Updating…
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={!!busy}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleSavePdf} disabled={!!busy || previewBusy}>
            <Icon icon={Download} em={1.2727} aria-hidden="true" />
            {busy === 'pdf' ? 'Generating…' : 'Save as PDF'}
          </Button>
          <Button onClick={handlePrint} disabled={!!busy || previewBusy}>
            <Icon icon={Printer} em={1.2727} aria-hidden="true" />
            {busy === 'print' ? 'Preparing…' : 'Print'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ═══ Headless capture sheet ═══
          Off-screen (fixed, far left), aria-hidden, non-interactive.
          Mounted only while actively rasterizing. White background is
          forced via the document-level data-theme flip in the effect
          above — this subtree never has to hand-duplicate tokens. */}
      {capture &&
        createPortal(
          <div
            aria-hidden="true"
            style={{ position: 'fixed', top: 0, left: -100000, pointerEvents: 'none' }}
          >
            <CaptureSheet
              ref={sheetRef}
              job={capture}
              onReady={() => handleCaptureReady(capture)}
            />
          </div>,
          document.body
        )}
    </Dialog>
  );
}
